export interface ProductSurfaceStorage {
  getItem(key: string): string | null;
}

export interface ProductSurfaceLocation {
  hash?: string;
  pathname?: string;
}

export type BrowserProductSurface = 'bos' | 'hos';

type RouteSurface = BrowserProductSurface | 'public' | 'stateful' | 'neutral' | null;

const BOS_ROUTE_ROOTS = [
  '/dashboard', '/office', '/calendar', '/kanban', '/tasks', '/board', '/canvas',
  '/paperclip', '/crm', '/linkedin', '/social', '/whatsapp', '/oc', '/coo',
  '/rascals', '/agents',
  // Legacy BOS-only admin routes redirect into the shared Settings surface.
  '/admin', '/hermes', '/chief', '/voice', '/brain', '/connectors', '/learning',
  '/self-healing', '/backup',
] as const;

function matchesRouteRoot(path: string, root: string): boolean {
  return path === root || path.startsWith(`${root}/`);
}

function surfaceFromPath(rawPath: string): RouteSurface {
  const path = rawPath.split('?', 1)[0].split('#', 1)[0];
  if (
    path === '/login'
    || path.startsWith('/login/')
    || path === '/onboarding'
    || path.startsWith('/onboarding/')
    || path === '/join'
    || path.startsWith('/join/')
  ) return 'public';
  if (path === '/hos' || path.startsWith('/hos/')) return 'hos';
  if (path === '/') return 'bos';
  if (BOS_ROUTE_ROOTS.some((root) => matchesRouteRoot(path, root))) return 'bos';
  if (matchesRouteRoot(path, '/settings')) return 'stateful';
  // Legacy health paths redirect according to edition/module availability, and
  // unknown paths redirect to login. Neither establishes a product surface.
  if (path.startsWith('/')) return 'neutral';
  return null;
}

function storedSurface(storage?: ProductSurfaceStorage | null): BrowserProductSurface | null {
  try {
    const stored = storage?.getItem('boss_active_product');
    return stored === 'bos' || stored === 'hos' ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Resolve a route path supplied by React Router. Unlike persisted product
 * state, the root route is an explicit BOS destination in combined installs.
 * Public routes stay neutral so login and activation requests do not inherit a
 * private product surface.
 */
export function productSurfaceFromRouterPath(rawPath: string): BrowserProductSurface | null {
  const routed = surfaceFromPath(rawPath);
  return routed === 'bos' || routed === 'hos' ? routed : null;
}

/**
 * Apply route authority only when that product is actually offered. This keeps
 * `/` BOS-authoritative in combined editions without changing a standalone HOS
 * installation into an unavailable product.
 */
export function resolveAvailableProductSurface(
  routed: BrowserProductSurface | null,
  active: BrowserProductSurface,
  available: readonly BrowserProductSurface[],
): BrowserProductSurface {
  return routed && available.includes(routed) ? routed : active;
}

/** Select a product value that is valid for the installed edition. */
export function selectAvailableProductSurface(
  preferred: BrowserProductSurface | null,
  fallback: BrowserProductSurface,
  available: readonly BrowserProductSurface[],
): BrowserProductSurface {
  if (preferred && available.includes(preferred)) return preferred;
  if (available.includes(fallback)) return fallback;
  return available[0] ?? fallback;
}

/**
 * Keep the shared shell on an installed product while a nested route guard is
 * redirecting an unavailable product URL. This prevents transient titles and
 * room artwork from advertising a product that is not installed.
 */
export function availableProductShellPath(
  rawPath: string,
  available: readonly BrowserProductSurface[],
): string {
  const path = rawPath.split('?', 1)[0].split('#', 1)[0] || '/';
  const routed = surfaceFromPath(path);
  if (routed === 'hos' && !available.includes('hos') && available.includes('bos')) return '/';
  if (routed === 'bos' && !available.includes('bos') && available.includes('hos')) return '/hos';
  return path;
}

/**
 * Resolve the product boundary from the route before consulting saved UI
 * state. HashRouter navigation commits the URL before React effects update
 * localStorage, so using storage alone can send the first HOS request with a
 * stale BOS header (or vice versa).
 */
export function currentBrowserProductSurface(
  storage?: ProductSurfaceStorage | null,
  locationValue: ProductSurfaceLocation | null | undefined = globalThis.location,
): BrowserProductSurface | null {
  const hash = locationValue?.hash ?? '';
  const hashPath = hash.startsWith('#') ? hash.slice(1) : hash;
  const routedFromHash = hashPath ? surfaceFromPath(hashPath) : null;
  if (routedFromHash === 'public') return null;
  if (routedFromHash === 'stateful') return storedSurface(storage);
  if (routedFromHash === 'neutral') return null;
  if (routedFromHash === 'bos' || routedFromHash === 'hos') return routedFromHash;

  const routedFromPath = surfaceFromPath(locationValue?.pathname ?? '');
  if (routedFromPath === 'public') return null;
  if (routedFromPath === 'stateful') return storedSurface(storage);
  if (routedFromPath === 'neutral') return null;
  if (routedFromPath === 'bos' || routedFromPath === 'hos') return routedFromPath;

  return storedSurface(storage);
}
